import cloneDeep from 'lodash/cloneDeep';

import type { AnswerReport } from '@webapp/account/src/resources/reports/answers';
import { initialCell, initialCol, initialRow } from '@webapp/account/src/resources/reports/reports';

import { BoolAnswerText, NpsType, roundFloat } from '@webapp/common/lib/ui';
import { striptags } from '@webapp/common/lib/utils';
import { PLACEHOLDER } from '@webapp/common/resources/fillers';
import { AnswerType, QuestionType } from '@webapp/common/resources/survey';

export interface SrvResponse {
    QUESTION_ID: number;
    ANSWER_ID: number;
    SURVEY_ID: number;
    RESPONSE_ID: number;
    COMMENT: string;
    EXPIRES: number;
    FILE_ID: number;
    ANSWER_GROUP_ID: number;
    ANSWER_STR: string;
    ANSWER_TEXT: string;
    ANSWER_BOOL: number;
    ANSWER_INT: number;
    CATEGORY_ID?: number;
    $file_info?: {
        EXT: string;
        ID: number;
        LINK: string;
        QUESTION_ID: number;
        REAL_NAME: string;
        RESPONSE_ID: number;
        SIZE: number;
        THUMB: string;
    };
}

const number = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    const response = responses.find(({ QUESTION_ID }: SrvResponse) => QUESTION_ID === answerReport.question.id);
    if (response) {
        answerReport.value = response.ANSWER_INT;
    }
    return answerReport;
};

const matrixAnswers = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.cols = answerReport.question.cols.map((col) => ({
        ...cloneDeep(initialCol),
        id: col.id,
        headValue: col.name,
    }));

    answerReport.rows = answerReport.question.rows.map(({ id, value, forwardParent }) => ({
        ...cloneDeep(initialRow),
        id,
        forwardParent,
        value1: String(value || ''),
        cells: answerReport.cols.map((col) => {
            const response = responses.find(
                ({ ANSWER_GROUP_ID, ANSWER_ID }: SrvResponse) => ANSWER_ID === id && col.id === ANSWER_GROUP_ID,
            );
            if (response && response.CANT_RATE) {
                return {
                    ...cloneDeep(initialCell),
                    checked: false,
                    cantRate: response.CANT_RATE,
                };
            } else if (response) {
                return {
                    ...cloneDeep(initialCell),
                    checked: response.ANSWER_BOOL === 1,
                    str: response.ANSWER_STR ? response.ANSWER_STR : null,
                };
            } else {
                return {
                    ...cloneDeep(initialCell),
                    checked: false,
                };
            }
        }),
    }));

    return answerReport;
};

const simpleValueList = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = responses
        .map(({ ANSWER_ID, ANSWER_STR }: SrvResponse) => {
            const answer = answerReport.question.rows.find(({ id }) => id === ANSWER_ID);

            if (!answer) {
                return null;
            }

            return {
                ...cloneDeep(initialRow),
                id: ANSWER_ID,
                forwardParent: answer.forwardParent,
                value1: String(answer.value || ''),
                singleValue: ANSWER_STR || '',
            };
        }, [])
        .filter((a) => Boolean(a));

    return answerReport;
};

const fileUpload = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = responses
        .map((resp: SrvResponse) => {
            const { $file_info: { EXT, ID, REAL_NAME, SIZE } = {} } = resp;

            if (!REAL_NAME) {
                // Ответ без данных о файле (файл удалён/не загружен) — строка просто
                // не попадает в отчёт. Раньше был console.error → шум в Sentry (FRONT-26SK).
                console.warn('no $file_info', resp);
                return null;
            }

            return {
                ...cloneDeep(initialRow),
                id: ID,
                value1: `${REAL_NAME}.${EXT}`,
                value2: SIZE,
            };
        })
        .filter(Boolean);
    return answerReport;
};

const tests = (answerReport: AnswerReport, { responses }: any, testScore: number): AnswerReport => {
    let totalScores = 0;

    const maxScore = answerReport.question.params.totalScore
        ? answerReport.question.params.testScore
        : answerReport.question.rows.reduce((acc, { value3: score }) => {
              // TODO move to common
              if (answerReport.question.type === QuestionType.TEST_ONE_OF_LIST) {
                  acc = Math.max(acc, score as number);
              } else {
                  acc += Math.max(0, score as number);
              }
              return acc;
          }, 0);

    answerReport.rows = responses
        .filter(({ ANSWER_BOOL }: SrvResponse) => ANSWER_BOOL === 1)
        .map((response: SrvResponse, index: number) => {
            const row = answerReport.question.rows.find(({ id }) => id === response.ANSWER_ID);
            if (row) {
                totalScores += Number(row.value3);
            }
            return {
                ...cloneDeep(initialRow),
                id: index + 1,
                forwardParent: row ? row.forwardParent : null,
                value1: row ? row.value : null,
            };
        });

    const isAllAnswered = responses.every((response: SrvResponse) => {
        const isSelected = response.ANSWER_BOOL;
        const row = answerReport.question.rows.find(({ id }) => id === response.ANSWER_ID);

        if ((isSelected && !row.value2) || (!isSelected && row.value2)) return false;

        return true;
    });

    answerReport.value = testScore;
    answerReport.value2 = roundFloat(maxScore, 1);
    answerReport.value3 = answerReport.question.params.totalScore
        ? isAllAnswered
            ? answerReport.question.params.testScore
            : 0
        : roundFloat(totalScores, 1);

    return answerReport;
};

const testText = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    const testScore = answerReport.question.params.testScore;
    answerReport.rows = responses
        .filter(({ ANSWER_BOOL }: SrvResponse) => ANSWER_BOOL === 1)
        .map((response: SrvResponse, index: number) => {
            return {
                ...cloneDeep(initialRow),
                id: index + 1,
                value1: response.ANSWER_STR,
            };
        });

    answerReport.value3 = answerReport.question.rows.find(
        (row) => String(row.value)?.toLowerCase() === String(answerReport.rows[0]?.value1)?.toLowerCase(),
    )
        ? testScore
        : 0;

    answerReport.value2 = roundFloat(testScore, 1);

    return answerReport;
};

const agreement = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    const response = responses.find(({ QUESTION_ID }: SrvResponse) => QUESTION_ID === answerReport.question.id);

    if (response) {
        // TODO do not use hardcoded values
        answerReport.value = response.ANSWER_BOOL === 1 ? BoolAnswerText.TRUE : BoolAnswerText.FALSE;
    }

    return answerReport;
};

const phone = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = responses
        .map(({ ANSWER_ID, ANSWER_STR, ANSWER_TEXT }: SrvResponse) => {
            const row = answerReport.question.rows.find(({ id }) => id === ANSWER_ID);
            const str = ANSWER_STR || '';
            const text = ANSWER_TEXT || '';

            if (!row) return null;

            return {
                ...cloneDeep(initialRow),
                ANSWER_ID,
                value1: String(row.value || ''),
                singleValue: text + str,
            };
        })
        .filter(Boolean);

    return answerReport;
};

const name = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = answerReport.question.rows.map(({ id, value, forwardParent }) => ({
        ...cloneDeep(initialRow),
        id: id,
        forwardParent,
        value1: String(value || ''),
        cells: answerReport.question.cols.map((col) => {
            const response = responses.find(
                ({ ANSWER_GROUP_ID, ANSWER_ID }: any) => ANSWER_ID === id && ANSWER_GROUP_ID === col.id,
            );

            return {
                ...cloneDeep(initialCell),
                str: response ? response.ANSWER_STR || '' : '',
            };
        }),
    }));

    answerReport.cols = answerReport.question.cols.map(({ id, name }) => ({
        ...cloneDeep(initialCol),
        id,
        headValue: name,
    }));

    return answerReport;
};

const residence = (answerReport: AnswerReport, dataAnswer: any): AnswerReport => {
    answerReport.rows = dataAnswer.responses.map(({ ANSWER_ID, ANSWER_TEXT }: any) => {
        const { value, forwardParent } = answerReport.question.rows.find(({ id }) => id === ANSWER_ID);
        return {
            ...cloneDeep(initialRow),
            id: ANSWER_ID,
            forwardParent,
            value1: String(value || ''),
            singleValue: ANSWER_TEXT || null,
        };
    });

    return answerReport;
};

const nestedList = (answerReport: AnswerReport, dataAnswer: any): AnswerReport => {
    answerReport.rows = dataAnswer.responses.map(({ ANSWER_ID, ANSWER_STR, ANSWER_TEXT }: any) => {
        const { value, forwardParent } = answerReport.question.rows.find(({ id }) => id === ANSWER_ID);
        return {
            ...cloneDeep(initialRow),
            id: ANSWER_ID,
            forwardParent,
            value1: String(value || ''),
            value2: parseInt(ANSWER_STR, 10), // nested item id
            singleValue: ANSWER_TEXT || null,
        };
    });

    return answerReport;
};

const groupFreeAnswers = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = answerReport.question.rows.map(({ id, value, forwardParent }) => {
        const resp = responses.find(({ ANSWER_ID }: any) => ANSWER_ID === id);
        return {
            ...cloneDeep(initialRow),
            id,
            forwardParent,
            value1: String(value || ''),
            value2: resp?.ANSWER_STR || '',
        };
    });

    return answerReport;
};

const text = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    const response = responses.find(({ QUESTION_ID }: any) => QUESTION_ID === answerReport.question.id);

    if (response) {
        answerReport.value = response.ANSWER_TEXT ? response.ANSWER_TEXT || '' : response.ANSWER_STR || '';

        if (!answerReport.value && response.CANT_RATE) {
            answerReport.value = PLACEHOLDER.CANT_RATE;
        }
    }
    return answerReport;
};

const semDiff = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.cols = answerReport.question.cols.map(({ id, name }) => ({
        ...cloneDeep(initialCol),
        id,
        headValue: name,
    }));

    answerReport.rows = answerReport.question.rows.map(({ id, value, value2, value3, forwardParent }) => ({
        ...cloneDeep(initialRow),
        id,
        value1: String(value || ''),
        value2: String(value2 || ''),
        value3: String(value3 || ''),
        forwardParent,
        cells: answerReport.cols.map((col) => {
            const response = responses.find(
                ({ ANSWER_GROUP_ID, ANSWER_ID }: any) => ANSWER_ID === id && col.id === ANSWER_GROUP_ID,
            );
            return {
                ...cloneDeep(initialCell),
                checked: response ? response.ANSWER_BOOL === 1 : false,
            };
        }),
    }));

    return answerReport;
};

const matrixDropdownList = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = answerReport.question.rows.map(({ id, value, value2, forwardParent }) => ({
        ...cloneDeep(initialRow),
        id,
        value1: String(value !== null ? value : ''),
        value2: String(value2 !== null ? value2 : ''),
        forwardParent,
        cells: answerReport.question.cols.map((col) => {
            const response = responses.find(
                ({ ANSWER_GROUP_ID, ANSWER_ID }: any) => ANSWER_ID === id && ANSWER_GROUP_ID === col.id,
            );

            return {
                ...cloneDeep(initialCell),
                str: response ? response.ANSWER_STR : null,
            };
        }),
    }));

    answerReport.cols = answerReport.question.cols.map(({ id, name }) => ({
        ...cloneDeep(initialCol),
        id,
        headValue: name,
    }));

    return answerReport;
};

const rank = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = responses.map(({ ANSWER_ID, ANSWER_INT }: any, index: number) => {
        const row = answerReport.question.rows.find(({ id }) => ANSWER_ID === id);
        return {
            ...cloneDeep(initialRow),
            id: index + 1,
            forwardParent: row.forwardParent,
            value1: row ? String(row.value || '') : '',
            order: ANSWER_INT,
        };
    });

    answerReport.rows = answerReport.rows.sort((a, b) => a.order - b.order);

    return answerReport;
};

const testRank = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    const testScore = answerReport.question.params.testScore;
    answerReport.rows = responses.map(({ ANSWER_ID, ANSWER_INT }: any, index: number) => {
        const row = answerReport.question.rows.find(({ id }) => ANSWER_ID === id);
        return {
            ...cloneDeep(initialRow),
            id: index + 1,
            forwardParent: row.forwardParent,
            value1: row ? String(row.value || '') : '',
            order: ANSWER_INT,
        };
    });

    answerReport.rows = answerReport.rows.sort((a, b) => a.order - b.order);

    answerReport.value3 = answerReport.question.rows.every((row, idx) => row.value === answerReport.rows[idx]?.value1)
        ? testScore
        : 0;

    answerReport.value2 = roundFloat(testScore, 1);

    return answerReport;
};

const nps = (answerReport: AnswerReport, { responses: [response] }: any): AnswerReport => {
    if (response) {
        let type: NpsType = null;
        const count = response.ANSWER_INT;

        if (!count && response.CANT_RATE) {
            type = NpsType.CANT_RATE;
        } else if (count <= 6) {
            type = NpsType.BAD;
        } else if (count > 6 && count <= 8) {
            type = NpsType.NORMAL;
        } else if (count > 8 && count <= 10) {
            type = NpsType.GOOD;
        }

        answerReport.rows = [
            {
                ...cloneDeep(initialRow),
                id: 1,
                count: count,
                npsType: type,
            },
        ];
    }

    return answerReport;
};

const distributeScale = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = answerReport.question.rows.map(({ id, value, forwardParent }) => {
        const response = responses.find(({ ANSWER_ID }: any) => ANSWER_ID === id);
        return {
            ...cloneDeep(initialRow),
            id,
            value1: String(value || ''),
            forwardParent,
            count: response ? response.ANSWER_INT : 0,
        };
    });
    return answerReport;
};

const smile = (answerReport: AnswerReport, { responses: [response] }: any): AnswerReport => {
    if (response) {
        const row = answerReport.question.rows.find(({ id }) => id === response.ANSWER_ID);
        answerReport.rows = row
            ? [
                  {
                      ...cloneDeep(initialRow),
                      id: row.id,
                      forwardParent: row.forwardParent,
                      value1: row.value,
                      value2: row.value2,
                      value3: row.value3,
                  },
              ]
            : [];
    }

    return answerReport;
};

const matrixRating = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = answerReport.question.rows.map(({ id, value, forwardParent }) => {
        const response = responses.find(({ ANSWER_ID }: any) => ANSWER_ID === id);

        return {
            ...cloneDeep(initialRow),
            id,
            forwardParent,
            count: response ? response.ANSWER_INT : 0,
            value1: String(value),
            cantRate: response?.CANT_RATE,
        };
    });
    return answerReport;
};

const csi = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    const groupWithOrder1 = answerReport.question.cols.find((col) => col.order === 1);
    const groupWithOrder2 = answerReport.question.cols.find((col) => col.order === 2);

    answerReport.rows = answerReport.question.rows.map(({ id, value, value2, value3, forwardParent }) => {
        const responseWithOrder1 = responses.find(
            ({ ANSWER_ID, ANSWER_GROUP_ID }: any) => ANSWER_ID === id && ANSWER_GROUP_ID === groupWithOrder1?.id,
        );
        const responseWithOrder2 = responses.find(
            ({ ANSWER_ID, ANSWER_GROUP_ID }: any) => ANSWER_ID === id && ANSWER_GROUP_ID === groupWithOrder2?.id,
        );
        // Ответ «Не могу оценить» сохраняется отдельной записью с ANSWER_GROUP_ID = null.
        const cantRateResponse = responses.find(({ ANSWER_ID, CANT_RATE }: any) => ANSWER_ID === id && CANT_RATE);

        return {
            ...cloneDeep(initialRow),
            id,
            count: responseWithOrder1 ? responseWithOrder1.ANSWER_INT : 0,
            importance: responseWithOrder2 ? responseWithOrder2.ANSWER_INT : 0,
            value1: String(value),
            value2: String(value2),
            value3: String(value3),
            forwardParent,
            comment: responseWithOrder1?.COMMENT || null,
            cantRate: cantRateResponse?.CANT_RATE,
        };
    });
    return answerReport;
};

const star = (answerReport: AnswerReport, { responses: [response] }: any): AnswerReport => {
    answerReport.rows = response
        ? [
              {
                  ...cloneDeep(initialRow),
                  id: response.RESPONSE_ID,
                  count: response.ANSWER_INT,
                  value1: answerReport.name,
                  cantRate: response.CANT_RATE,
              },
          ]
        : [];

    return answerReport;
};

const scale = (answerReport: AnswerReport, { responses: [response] }: any): AnswerReport => {
    if (response) {
        const answerGroupId = response.ANSWER_GROUP_ID;
        const col: any = answerReport.question.cols.find(({ id }) => id === answerGroupId);
        if (col) {
            let value1 = col.name;
            if (answerReport.question.params.scaleType === 'number') {
                value1 = typeof col.numeric_value === 'number' ? col.numeric_value : value1;
            }
            value1 = typeof col.numeric_value === 'number' ? value1 ?? col.numeric_value : value1;

            answerReport.rows = [
                {
                    ...cloneDeep(initialRow),
                    id: response.RESPONSE_ID,
                    value1,
                },
            ];
        }

        if (response.CANT_RATE) {
            answerReport.rows = [
                {
                    ...cloneDeep(initialRow),
                    id: response.RESPONSE_ID,
                    value1: PLACEHOLDER.CANT_RATE,
                },
            ];
        }
    }

    return answerReport;
};

const oneOfList = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = responses
        .filter(({ ANSWER_BOOL }): any => Boolean(ANSWER_BOOL))
        .map(({ ANSWER_ID, ANSWER_TEXT }: any) => {
            const answer = answerReport.question.rows.find(({ id }) => id === ANSWER_ID);

            if (!answer) {
                return null;
            }

            return {
                ...cloneDeep(initialRow),
                id: ANSWER_ID,
                value1: answer.value,
                forwardParent: answer.forwardParent,
                value2: answer.type === AnswerType.SELF ? ANSWER_TEXT : null,
                type: answer.type,
            };
        })
        .filter(Boolean);

    return answerReport;
};

const dropdownList = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    if (responses.length > 0) {
        answerReport.rows = responses.map((response) => {
            const row = answerReport.question.rows.find(({ id }) => id === response.ANSWER_ID);
            return {
                ...cloneDeep(initialRow),
                id: response.RESPONSE_ID,
                forwardParent: row.forwardParent,
                value1: response.ANSWER_STR || '',
            };
        });
    }
    return answerReport;
};

const selectImage = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = responses
        .filter(({ ANSWER_BOOL }: any) => ANSWER_BOOL === 1)
        .map(({ ANSWER_ID }: any) => {
            const row = answerReport.question.rows.find(({ id }) => id === ANSWER_ID);

            return {
                ...cloneDeep(initialRow),
                id: ANSWER_ID,
                forwardParent: row.forwardParent,
                value1: row ? row.filePath : null,
                value2: row ? row.value : null,
            };
        });
    return answerReport;
};

const fewOfList = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = responses
        .filter(({ ANSWER_BOOL }: any) => ANSWER_BOOL === 1)
        .map(({ ANSWER_ID, ANSWER_TEXT }: any) => {
            const row = answerReport.question.rows.find(({ id }) => id === ANSWER_ID);

            if (!row) return null;

            return {
                ...cloneDeep(initialRow),
                id: ANSWER_ID,
                value1: row.value,
                forwardParent: row.forwardParent,
                value2: row.type === AnswerType.SELF ? ANSWER_TEXT : null,
                type: row.type,
            };
        })
        .filter(Boolean);

    return answerReport;
};

const abTesting = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = responses
        .filter(({ ANSWER_BOOL }: any) => ANSWER_BOOL === 1)
        .map(({ ANSWER_ID }: any) => {
            const row = answerReport.question.rows.find(({ id }) => id === ANSWER_ID);

            if (!row) return null;

            return {
                ...cloneDeep(initialRow),
                id: ANSWER_ID,
                forwardParent: row.forwardParent,
                value1: row.value,
                value2: row.value2,
                type: row.type,
                textBlock: row.textBlock,
                filePath: row.filePath,
            };
        })
        .filter(Boolean);

    return answerReport;
};

const textPerception = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = responses.map(({ ANSWER_JSON }: any) => {
        return {
            ...cloneDeep(initialRow),
            value2:
                JSON.parse(ANSWER_JSON)?.reduce((acc, item) => {
                    acc[item.pos] = item.word;
                    return acc;
                }, {}) || {},
        };
    });

    return answerReport;
};

const pairComparison = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = responses
        .map(({ ANSWER_ID, ANSWER_BOOL }: any) => {
            const answer = answerReport.question.rows.find(({ id }) => id === ANSWER_ID);

            if (!answer || !ANSWER_BOOL) {
                return null;
            }

            return {
                ...cloneDeep(initialRow),
                id: ANSWER_ID,
                value1: answer.textBlock,
                filePath: answer.filePath,
                type: answer.type,
            };
        })
        .filter(Boolean);
    return answerReport;
};

const cardSorting = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.cols = answerReport.question.categories.map((category) => ({
        ...cloneDeep(initialCol),
        id: category.id,
        headValue: category.name,
    }));

    answerReport.rows = answerReport.question.rows.map(({ id, value, forwardParent }) => ({
        ...cloneDeep(initialRow),
        id,
        forwardParent,
        value1: String(value || ''),
        cells: answerReport.question.categories.map((category) => {
            const response = responses.find(
                ({ ANSWER_ID, CATEGORY_ID }: SrvResponse) => ANSWER_ID === id && category.id === CATEGORY_ID,
            );

            return {
                ...cloneDeep(initialCell),
                checked: Boolean(response),
            };
        }),
    }));

    return answerReport;
};

const maxDiff = (answerReport: AnswerReport, _: any): AnswerReport => {
    return answerReport;
};

const kano = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = answerReport.question.rows.map((row) => {
        const response = responses.find(({ ANSWER_ID }: any) => ANSWER_ID === row.id);
        return {
            ...cloneDeep(initialRow),
            id: row.id,
            value1: row.value,
            value2: row.value2,
            value3: row.value3,
            singleValue: response?.ANSWER_JSON || null,
            comment: response?.COMMENT || null,
        };
    });

    return answerReport;
};

const figmaTest = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    if (!responses || responses.length === 0) {
        return answerReport;
    }

    const response = responses[0];
    const { ANSWER_JSON, ANSWER_BOOL, ANSWER_INT, ANSWER_TEXT } = response;
    let parsedJson: {
        events?: Array<any>;
        path?: Array<any>;
        videoFileId?: number;
        audioFileId?: number;
        cameraFileId?: number;
    } = {};

    try {
        parsedJson = JSON.parse(ANSWER_JSON);
    } catch (e) {
        // ignore
    }

    answerReport.rows = [
        {
            ...cloneDeep(initialRow),
            id: 1,
            value1:
                ANSWER_BOOL === 1
                    ? 'Успешно'
                    : ANSWER_BOOL === 0
                    ? 'Сдался'
                    : ANSWER_BOOL === 2
                    ? 'Время истекло'
                    : 'Exploratory',
            value2: ANSWER_TEXT || '',
            time: ANSWER_INT,
            singleValue: ANSWER_BOOL,
        },
    ];

    answerReport.addInfo = {
        ...(answerReport.addInfo || {}),
        figmaEvents: parsedJson.events || [],
        figmaPath: parsedJson.path || [],
        figmaVideoFileId: parsedJson.videoFileId || null,
        figmaAudioFileId: parsedJson.audioFileId || null,
        figmaCameraFileId: parsedJson.cameraFileId || null,
    };

    return answerReport;
};

const firstClick = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = responses.map(({ ANSWER_JSON, TIME, ANSWER_INT, ANSWER_ID, CANT_RATE, RESPONSE_ID }: any) => {
        // Ответ «Не могу оценить» приходит без координат клика (ANSWER_JSON = null).
        if (CANT_RATE || !ANSWER_JSON) {
            return {
                ...cloneDeep(initialRow),
                id: RESPONSE_ID,
                value1: PLACEHOLDER.CANT_RATE,
            };
        }

        const { x, y } = JSON.parse(ANSWER_JSON);
        const foundedTargetAreaName = answerReport.question.rows.find((row) => row.id === ANSWER_ID)?.value;
        return {
            ...cloneDeep(initialRow),
            x,
            y,
            time: TIME,
            answerId: ANSWER_ID,
            order: ANSWER_INT,
            targetAreaName: foundedTargetAreaName ? striptags(foundedTargetAreaName) : '',
        };
    });

    return answerReport;
};

const conjoint = (answerReport: AnswerReport, { responses }: any): AnswerReport => {
    answerReport.rows = responses
        .filter(({ ANSWER_BOOL }) => Boolean(ANSWER_BOOL))
        .map(({ ANSWER_ID }) => {
            let card;
            answerReport?.question?.conjointConfig?.tasks.find((task) =>
                task.cards.find((itemCard) => {
                    if (itemCard.id === ANSWER_ID) {
                        card = itemCard;
                    }
                }),
            );

            return {
                ...cloneDeep(initialRow),
                id: card.id,
                value1: card?.name,
                value2: card?.levels
                    .map((item) => {
                        return `${striptags(item.attributeName)}: ${striptags(item.levelName)}`;
                    })
                    .join(' / '),
            };
        });

    return answerReport;
};

export const mapAnswerReportResp = (
    answerReport: AnswerReport,
    dataAnswers: Array<any>,
    testScore: number,
): AnswerReport => {
    const dataAnswer: any = dataAnswers.find(({ id }) => id === answerReport.id);
    const notAnswered = !dataAnswer || dataAnswer.responses.length === 0;

    answerReport.comment = notAnswered ? null : dataAnswer.responses[0].COMMENT;

    if (notAnswered) {
        answerReport.cols = [];
        answerReport.rows = [];
        answerReport.value = null;
        return answerReport;
    }

    switch (answerReport.question.type) {
        case QuestionType.NUMBER:
            return number(answerReport, dataAnswer);
        case QuestionType.MATRIX_FEW_ANSWERS:
        case QuestionType.MATRIX_SINGLE_ANSWER:
            return matrixAnswers(answerReport, dataAnswer);
        case QuestionType.MATRIX_RATING:
            return matrixRating(answerReport, dataAnswer);
        case QuestionType.CSI:
            return csi(answerReport, dataAnswer);
        case QuestionType.STAR:
            return star(answerReport, dataAnswer);
        case QuestionType.RANK:
            return rank(answerReport, dataAnswer);
        case QuestionType.TEST_RANK:
            return testRank(answerReport, dataAnswer);
        case QuestionType.DISTRIBUTE_SCALE:
            return distributeScale(answerReport, dataAnswer);
        case QuestionType.SMILE:
            return smile(answerReport, dataAnswer);
        case QuestionType.NESTED_LIST:
            return nestedList(answerReport, dataAnswer);
        case QuestionType.RESIDENCE:
            return residence(answerReport, dataAnswer);
        case QuestionType.SEM_DIFF:
            return semDiff(answerReport, dataAnswer);
        case QuestionType.NPS:
            return nps(answerReport, dataAnswer);
        case QuestionType.SCALE:
            return scale(answerReport, dataAnswer);
        case QuestionType.SELECT_FEW_IMAGE:
        case QuestionType.SELECT_ONE_IMAGE:
            return selectImage(answerReport, dataAnswer);
        case QuestionType.TEST_FEW_OF_LIST:
        case QuestionType.TEST_ONE_OF_LIST:
            return tests(answerReport, dataAnswer, testScore);
        case QuestionType.TEST_TEXT:
            return testText(answerReport, dataAnswer);
        case QuestionType.AGREEMENT:
            return agreement(answerReport, dataAnswer);
        case QuestionType.FEW_OF_LIST:
            return fewOfList(answerReport, dataAnswer);
        case QuestionType.ONE_OF_LIST:
            return oneOfList(answerReport, dataAnswer);
        case QuestionType.DROPDOWN_LIST:
            return dropdownList(answerReport, dataAnswer);
        case QuestionType.PHONE:
            return phone(answerReport, dataAnswer);
        case QuestionType.DATETIME:
        case QuestionType.EMAIL:
            return simpleValueList(answerReport, dataAnswer);
        case QuestionType.NAME:
            return name(answerReport, dataAnswer);
        case QuestionType.GROUP_FREE_ANSWERS:
            return groupFreeAnswers(answerReport, dataAnswer);
        case QuestionType.MATRIX_DROPDOWN_LIST:
        case QuestionType.MATRIX_TEXT_ANSWER:
            return matrixDropdownList(answerReport, dataAnswer);
        case QuestionType.LONG_TEXT:
        case QuestionType.SHORT_TEXT:
            return text(answerReport, dataAnswer);
        case QuestionType.FILE_UPLOAD:
            return fileUpload(answerReport, dataAnswer);
        case QuestionType.AB_TESTING:
            return abTesting(answerReport, dataAnswer);
        case QuestionType.TEXT_PERCEPTION:
            return textPerception(answerReport, dataAnswer);
        case QuestionType.PAIR_COMPARISON:
            return pairComparison(answerReport, dataAnswer);
        case QuestionType.FIRST_CLICK:
            return firstClick(answerReport, dataAnswer);
        case QuestionType.CARD_SORTING:
            return cardSorting(answerReport, dataAnswer);
        case QuestionType.MAX_DIFF:
            return maxDiff(answerReport, dataAnswer);
        case QuestionType.KANO:
            return kano(answerReport, dataAnswer);
        case QuestionType.FIGMA_TEST:
            return figmaTest(answerReport, dataAnswer);
        case QuestionType.CONJOINT:
            return conjoint(answerReport, dataAnswer);
        case QuestionType.TEXT_BLOCK:
        default:
            return answerReport;
    }
};
